import useFormContext from '@src/shared/form/useFormContext';
import { Button, IconButton } from '@src/shared/ui/Button';
import { DeleteIcon, UploadIcon } from '@src/shared/ui/icons';
import { Box, Image } from '@src/shared/ui/layout';
import type { ChangeEvent } from 'react';

const StyleSheet = {
  uploadArea: {
    alignItems: 'center',
    backgroundColor: 'grey.100',
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    width: '100%',
  },
  uploadButton: {
    backgroundColor: 'common.white',
    borderColor: 'grey.600',
    height: '40px',
    width: '125px',
  },
  previewWrapper: {
    height: '100%',
    position: 'relative',
    width: '100%',
  },
  previewImage: {
    display: 'block',
    height: '100%',
    objectFit: 'cover',
    width: '100%',
  },
  deleteButton: {
    color: 'common.white',
    left: '50%',
    position: 'absolute',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    '& .MuiSvgIcon-root': {
      color: 'common.white',
    },
  },
};

type FormContextShape = {
  setValue: (
    name: string,
    value: unknown,
    options?: { shouldDirty?: boolean; shouldValidate?: boolean },
  ) => void;
  watch: (name: string) => unknown;
};

type ImageUploadAreaFieldProps = {
  name: string;
  disabled?: boolean;
};

function ImageUploadAreaField({
  name,
  disabled = false,
}: ImageUploadAreaFieldProps) {
  const { setValue, watch } = useFormContext() as FormContextShape;
  const previewName = `${name}Preview`;
  const imagePreview = watch(previewName) as string | null;

  const clearImage = () => {
    setValue(name, null, { shouldDirty: true, shouldValidate: true });
    setValue(previewName, null, { shouldDirty: true, shouldValidate: true });
  };

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;

    setValue(name, file, { shouldDirty: true, shouldValidate: true });

    if (!file) {
      setValue(previewName, null, { shouldDirty: true, shouldValidate: true });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setValue(
        previewName,
        typeof reader.result === 'string' ? reader.result : null,
        { shouldDirty: true, shouldValidate: true },
      );
    };
    reader.readAsDataURL(file);
  };

  if (imagePreview) {
    return (
      <Box sx={StyleSheet.previewWrapper}>
        <Image src={imagePreview} sx={StyleSheet.previewImage} />
        <IconButton
          disabled={disabled}
          onClick={clearImage}
          sx={StyleSheet.deleteButton}
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box sx={StyleSheet.uploadArea}>
      <Button
        component="label"
        variant="outlined"
        disabled={disabled}
        sx={StyleSheet.uploadButton}
      >
        <UploadIcon color="inherit" />
        <input
          hidden
          type="file"
          accept="image/*"
          onChange={handleImageChange}
        />
      </Button>
    </Box>
  );
}

export default ImageUploadAreaField;
